import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Calendar, ArrowUpRight } from "lucide-react";
import { API } from "../App";
import TrustStars from "./TrustStars";
import { countryToFlag } from "../lib/countryFlag";
import FollowTeamButton from "./FollowTeamButton";

/**
 * Premium "This Weekend's Big Slate" card.
 * Shows 4-5★ Trust Score picks across elite leagues for the upcoming
 * Saturday/Sunday — designed for Friday-night homepage browsing.
 *
 * Self-hides when the window has zero qualifying picks.
 */
const formatKickoff = (iso) => {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      weekday: "short",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
  } catch {
    return "";
  }
};

const formatWeekendRange = (sat, sun) => {
  if (!sat || !sun) return "";
  try {
    const satD = new Date(`${sat}T00:00:00Z`);
    const sunD = new Date(`${sun}T00:00:00Z`);
    const opts = { day: "numeric", month: "short" };
    return `${satD.toLocaleDateString(undefined, opts)} – ${sunD.toLocaleDateString(undefined, opts)}`;
  } catch {
    return `${sat} – ${sun}`;
  }
};

export const WeekendBigSlate = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data: payload } = await axios.get(
          `${API}/weekend/big-slate?min_trust=4&limit=12`,
        );
        if (!cancelled) setData(payload);
      } catch (_) {
        // Silent — card just stays hidden
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (loading) return null;
  if (!data || !data.picks || data.picks.length === 0) return null;

  const picks = data.picks;
  const range = formatWeekendRange(data.weekend?.sat, data.weekend?.sun);
  const elite5 = picks.filter((p) => p.market.trust_score === 5).length;
  const trustRelaxed = !!data.trust_relaxed;
  const effectiveFloor = data.effective_min_trust || data.min_trust;

  return (
    <section
      className="relative overflow-hidden rounded-3xl border border-amber-400/30 bg-gradient-to-br from-amber-500/[0.08] via-background to-emerald-500/[0.06] p-6 sm:p-8 mt-6 mb-10"
      data-testid="weekend-big-slate"
    >
      {/* Decorative glow */}
      <div className="pointer-events-none absolute -top-20 -right-16 h-64 w-64 rounded-full bg-amber-400/20 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-20 -left-16 h-56 w-56 rounded-full bg-emerald-500/15 blur-3xl" />

      {/* Header */}
      <div className="relative flex flex-wrap items-end justify-between gap-3 mb-6">
        <div>
          <h3 className="font-display text-2xl sm:text-3xl font-bold tracking-tight">
            {elite5 > 0
              ? `${elite5} elite 5★ Trust pick${elite5 === 1 ? "" : "s"}.`
              : `${picks.length} high-trust picks for the weekend.`}
          </h3>
          <p className="mt-1 text-sm text-muted-foreground inline-flex items-center gap-1.5 flex-wrap">
            <Calendar size={13} />
            <span data-testid="weekend-range">{range}</span>
            <span className="opacity-50">·</span>
            <span>{data.fixtures_in_window} elite fixtures · {data.leagues?.length || 0} leagues</span>
            {trustRelaxed && (
              <>
                <span className="opacity-50">·</span>
                <span
                  className="inline-flex items-center gap-1 rounded-full border border-amber-400/40 bg-amber-400/10 px-2 py-0.5 text-[10px] font-semibold text-amber-700 dark:text-amber-300"
                  data-testid="weekend-trust-relaxed"
                  title="Trust floor relaxed because no 4★ picks were ready yet — showing the strongest available."
                >
                  {effectiveFloor}★ floor
                </span>
              </>
            )}
          </p>
        </div>
        <button
          type="button"
          onClick={() => navigate("/weekend")}
          className="inline-flex items-center gap-1.5 text-xs font-semibold text-amber-700 dark:text-amber-400 hover:text-amber-600 transition-colors group"
          data-testid="weekend-cta-all-picks"
        >
          See all weekend picks
          <ArrowUpRight size={14} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
        </button>
      </div>

      {/* Picks grid */}
      <div className="relative grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {picks.map((p, idx) => {
          const m = p.match;
          const mk = p.market;
          const fid = p.fixture_id;
          const trust = mk.trust_score;
          const conf = mk.confidence;
          return (
            <div
              key={`${fid}-${idx}`}
              role="button"
              tabIndex={0}
              onClick={() => navigate(`/match/${fid}`)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  navigate(`/match/${fid}`);
                }
              }}
              className="group relative text-left rounded-2xl border border-border bg-card/80 backdrop-blur-sm hover:border-amber-400/50 hover:bg-card transition-all p-4 cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-400/60"
              data-testid={`weekend-pick-${fid}-${idx}`}
            >
              {/* Top row: league + kickoff */}
              <div className="flex items-center justify-between text-[10px] font-bold tracking-[0.08em] uppercase text-muted-foreground mb-2">
                <span className="truncate">
                  {countryToFlag(m.league?.country)} {m.league?.name}
                </span>
                <span className="font-tabular shrink-0">{formatKickoff(p.kickoff)}</span>
              </div>

              {/* Teams */}
              <div className="font-display text-sm font-semibold leading-snug mb-3 line-clamp-2 flex items-center gap-1 flex-wrap">
                <span className="truncate">{m.home_team?.name}</span>
                <span onClick={(e) => e.stopPropagation()} className="shrink-0">
                  <FollowTeamButton teamId={m.home_team?.id} teamName={m.home_team?.name} size="sm" />
                </span>
                <span className="text-muted-foreground font-normal">vs</span>
                <span className="truncate">{m.away_team?.name}</span>
                <span onClick={(e) => e.stopPropagation()} className="shrink-0">
                  <FollowTeamButton teamId={m.away_team?.id} teamName={m.away_team?.name} size="sm" />
                </span>
              </div>

              {/* Pick + trust */}
              <div className="flex items-center justify-between gap-2 pt-2 border-t border-border/50">
                <div className="min-w-0">
                  <div className="text-[10px] font-bold tracking-[0.08em] uppercase text-muted-foreground truncate">
                    {mk.market_type}
                  </div>
                  <div className="text-sm font-semibold truncate">{mk.prediction}</div>
                </div>
                <div className="flex flex-col items-end gap-0.5 shrink-0">
                  <span className="font-tabular font-bold text-base text-amber-700 dark:text-amber-400">
                    {Math.round(conf)}%
                  </span>
                  <TrustStars
                    score={trust}
                    components={mk.trust_components}
                    size="xs"
                    testId={`weekend-trust-${fid}-${idx}`}
                  />
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
};

export default WeekendBigSlate;
